import { NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../../core/auth/auth';
import {
  Competition,
  CompetitionsService,
  LeaderboardAward,
  LeaderboardEntry,
  awardValue,
  prizeDescriptionsFor,
} from '../../../core/competitions/competitions';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** F-017 — a card only shows the N most recently-awarded achievements,
 *  not the full history — this is a glanceable summary, not the ledger
 *  (that's what the competition detail page's picks table is for). */
const RECENT_ACHIEVEMENTS_LIMIT = 3;

@Component({
  selector: 'app-competition-list',
  imports: [RouterLink, NgTemplateOutlet],
  templateUrl: './competition-list.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-list.css',
})
export class CompetitionList implements OnInit {
  private readonly competitions = inject(CompetitionsService);
  private readonly auth = inject(AuthService);

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly owned = signal<Competition[]>([]);
  readonly joined = signal<Competition[]>([]);
  readonly awardValue = awardValue;

  /** F-017 — the viewer's own leaderboard entry (points/awards) per
   *  competition, keyed by competition id. Owned and joined both imply
   *  the viewer is a contestant (owners are auto-enrolled, decision #10),
   *  so every card here always has one once loaded. */
  readonly myEntries = signal<Map<number, LeaderboardEntry | null>>(new Map());

  /** F-014 fix — "Admin-owned" is admin-facing info (which pools other
   *  site admins run), not something a regular contestant needs to see
   *  about the pool they joined. It never shows in the "you own" section
   *  either — that's redundant, you already know your own competitions. */
  readonly isSiteAdmin = computed(
    () => this.auth.user()?.siteRole === 'SITE_ADMIN',
  );

  async ngOnInit(): Promise<void> {
    try {
      const result = await this.competitions.listMine();
      this.owned.set(result.owned);
      this.joined.set(result.joined);

      // F-017 — one leaderboard fetch per card. Sequential, not
      // Promise.all — keeps zoneless test timing predictable (matches the
      // single-await-per-request pattern used elsewhere, e.g.
      // CompetitionDetail.load()). Each fetch already succeeds for any
      // member (owner/cohost/contestant), and one card's fetch failing
      // shouldn't blank out the whole page.
      const userId = this.auth.user()?.id;
      const entries = new Map<number, LeaderboardEntry | null>();
      for (const c of [...result.owned, ...result.joined]) {
        try {
          const leaderboard = await this.competitions.getLeaderboard(c.id);
          entries.set(c.id, leaderboard.find((e) => e.userId === userId) ?? null);
        } catch {
          entries.set(c.id, null);
        }
      }
      this.myEntries.set(entries);
    } catch {
      this.errorMessage.set('Could not load your competitions — try reloading the page.');
    } finally {
      this.loading.set(false);
    }
  }

  /** Dates come back as full ISO timestamps — only the date portion is meaningful here. */
  dateOnly(iso: string): string {
    return iso.slice(0, 10);
  }

  /** "N days left to submit picks" while the submission window is open,
   *  else "Monitoring for achievements" — the competition doesn't stop
   *  existing at the cutoff, it just switches from drafting to watching
   *  for deaths/awards. */
  submissionStatus(c: Competition): string {
    const daysLeft = Math.ceil(
      (new Date(c.submissionEndDate).getTime() - Date.now()) / MS_PER_DAY,
    );
    if (daysLeft > 0) {
      return `${daysLeft} day${daysLeft === 1 ? '' : 's'} left to submit picks`;
    }
    return 'Monitoring for achievements';
  }

  totalPointsFor(c: Competition): number {
    return this.myEntries().get(c.id)?.totalPoints ?? 0;
  }

  financialAwardsFor(c: Competition): string[] {
    return prizeDescriptionsFor(this.myEntries().get(c.id)?.awards ?? []);
  }

  recentAchievementsFor(c: Competition): LeaderboardAward[] {
    const awards = this.myEntries().get(c.id)?.awards ?? [];
    return [...awards]
      .sort((a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime())
      .slice(0, RECENT_ACHIEVEMENTS_LIMIT);
  }
}
